/* ======================================================================
   COLLECTION SHOP THE LOOK — R6 (DRAFT)
   ----------------------------------------------------------------------
   Opens and closes one look at a time.

   The previous version bound a click handler to every `.shop-look-banner`
   in its own constructor and looked the panel up by an attribute the tile
   was never given, so the handler ran, found null and returned. Nothing
   was clickable and nothing errored.

   This one delegates from the document instead, so it does not care when
   the tiles were rendered, and it fails loudly in the console rather than
   silently if a trigger points at a look that is not on the page.
   ====================================================================== */

(() => {
  if (customElements.get('shop-look-drawer')) return;

  const OPEN_CLASS = 'is-open';
  const DRAG_CLASS = 'is-dragging';
  const LOCK_CLASS = 'shop-look-open';

  /* How far down the sheet has to be dragged, or how fast it has to be
     flicked, before the release counts as a dismissal rather than a
     bounce-back.

     A flick also has to have travelled MIN_PX. Without that floor, a
     press with a few pixels of jitter in it dismisses the sheet: the
     pointer events can land in the same millisecond, and dividing even a
     5px wobble by 1ms clears any velocity threshold. */
  const DISMISS_DISTANCE = 0.25;
  const DISMISS_VELOCITY = 0.5;
  const DISMISS_MIN_PX = 48;

  let openDrawer = null;
  let scrollY = 0;

  const lockScroll = () => {
    scrollY = window.scrollY;
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.style.top = `-${scrollY}px`;
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
  };

  class ShopLookDrawer extends HTMLElement {
    connectedCallback() {
      if (this.__bound) return;
      this.__bound = true;

      this.panel = this.querySelector('.shop-look__panel');
      this.backdrop = this.querySelector('[data-shop-look-backdrop]');
      this.grip = this.querySelector('[data-shop-look-grip]');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-shop-look-close]') || event.target === this.backdrop) {
          this.close();
        }
      });

      this.bindDrag();
    }

    open(trigger) {
      if (openDrawer && openDrawer !== this) openDrawer.close({ keepLock: true });

      this.__trigger = trigger || null;
      this.hidden = false;

      /* The panel starts translated off-screen. Reading a layout property
         between removing `hidden` and adding the open class forces the
         browser to accept the closed position as the transition's start,
         instead of collapsing both into one frame and skipping it. */
      void this.offsetHeight;

      this.classList.add(OPEN_CLASS);
      openDrawer = this;
      lockScroll();

      if (this.panel) this.panel.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKeydown);
    }

    close({ keepLock = false } = {}) {
      if (!this.classList.contains(OPEN_CLASS)) return;

      this.classList.remove(OPEN_CLASS, DRAG_CLASS);
      if (this.panel) this.panel.style.transform = '';
      document.removeEventListener('keydown', this.onKeydown);

      const finish = () => {
        this.hidden = true;
      };

      /* Wait for the slide-out so the panel is not yanked out of the
         layout mid-transition. */
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        setTimeout(finish, 320);
      }

      if (openDrawer === this) openDrawer = null;
      if (!keepLock) unlockScroll();

      if (this.__trigger && document.contains(this.__trigger)) {
        this.__trigger.focus({ preventScroll: true });
      }
      this.__trigger = null;
    }

    onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    };

    /* ------------------------------------------------------------------
       Swipe down to dismiss, on the sheet's header only.
       Dragging from the list would fight the list's own scrolling.
       ------------------------------------------------------------------ */
    bindDrag() {
      if (!this.grip || !this.panel) return;

      let startY = null;
      let startTime = 0;
      let delta = 0;

      const isSheet = () => matchMedia('(max-width: 999px)').matches;

      this.grip.addEventListener(
        'pointerdown',
        (event) => {
          if (!isSheet() || event.button !== 0) return;
          startY = event.clientY;
          startTime = event.timeStamp;
          delta = 0;
          this.classList.add(DRAG_CLASS);
          this.grip.setPointerCapture(event.pointerId);
        },
        { passive: true }
      );

      this.grip.addEventListener('pointermove', (event) => {
        if (startY === null) return;
        delta = Math.max(0, event.clientY - startY);
        this.panel.style.transform = `translateY(${delta}px)`;
      });

      const release = (event) => {
        if (startY === null) return;

        const height = this.panel.offsetHeight || 1;
        const elapsed = Math.max(1, event.timeStamp - startTime);
        const velocity = delta / elapsed;

        startY = null;
        this.classList.remove(DRAG_CLASS);
        this.panel.style.transform = '';

        const draggedFar = delta / height > DISMISS_DISTANCE;
        const flicked = delta > DISMISS_MIN_PX && velocity > DISMISS_VELOCITY;

        if (draggedFar || flicked) {
          this.close();
        }
      };

      this.grip.addEventListener('pointerup', release);
      this.grip.addEventListener('pointercancel', release);
    }
  }

  customElements.define('shop-look-drawer', ShopLookDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-shop-look-open]');
    if (!trigger) return;

    const id = trigger.dataset.shopLookOpen;
    const drawer = document.querySelector(`shop-look-drawer[data-look-id="${id}"]`);

    if (!drawer) {
      console.warn(`[shop-the-look] no drawer on the page for look "${id}"`);
      return;
    }

    event.preventDefault();
    drawer.open(trigger);
  });
})();
